import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import MainHeader, { MENU_REPORT } from "../../components/common/MainHeader";
import { WOOD_IMPORT_GRADE_DISPLAY } from "../../constance/dataTypes";
import ReportDetail from "../../components/reports/ReportDetail";
import { URL_BARCODE, URL_SCALE_LARGE } from "../../constance/urls";
import { formatComma } from "../../utils/stringFormat";
import useSearchItem from "../../hooks/useSeatchItem";
import ReportExportButton from "../../components/common/ReportExportButton";
import {
  AutoComplete,
  Row,
  Col,
  DatePicker,
  Select,
  Layout,
  Form,
  Typography,
  Input,
  Spin,
} from "antd";
import { DATE_FORMAT, HUMAN_DATE_FORMAT } from "../../constance/strings";
import { useDebounce } from "use-debounce";
import dayjs from "dayjs";
import { Content } from "antd/es/layout/layout";
import { LoadingOutlined, SearchOutlined } from "@ant-design/icons";

export default function ReportWoodImportByWoodThick() {
  const { t } = useTranslation();
  const [queryParams, setQueryParams] = useState(null);
  const [form] = Form.useForm();
  const date = Form.useWatch("dateRange", form);
  const grade = Form.useWatch("wood_grade_options", form);
  const [errors, setErrors] = useState({});

  const ref = useRef();

  const columns = React.useMemo(
    () => [
      {
        title: t("log_wood_evaluation_datas.log_wood_diameter"),
        dataIndex: "mil_thick_code",
      },
      {
        title: "รหัสสินค้า",
        dataIndex: "wood_code",
      },
      {
        title: "จำนวนส่ง (ท่อน)",
        dataIndex: "total_woods",
        render: (text, record) => formatComma(record.total_woods, 0),
      },
      {
        title: "ไม้ดี (ท่อน)",
        dataIndex: "good_woods",
        render: (text, record) => formatComma(record.good_woods, 0),
      },
      {
        title: "ปริมาตร (ลบ.ฟ.)",
        dataIndex: "good_volume",
        render: (text, record) => formatComma(record.good_volume, 4),
      },
      {
        title: "%",
        dataIndex: "percentage",
        render: (text, record) => formatComma(record.percentage),
      },
      {
        title: "ไม้เสีย (ท่อน)",
        dataIndex: "waste_woods",
        render: (text, record) => formatComma(record.waste_woods, 0),
      },
      {
        title: "ปริมาตร (ลบ.ฟ.)",
        dataIndex: "waste_volume",
        render: (text, record) => formatComma(record.waste_volume, 4),
      },
      {
        title: "ปริมาตรทั้งหมด (ลบ.ฟ.)",
        dataIndex: "",
        render: (text, record) => formatComma(record.total_volume, 4),
      },
      {
        title: "จำนวนเงินซื้อ (บาท)",
        dataIndex: "buy_amount",
        render: (text, record) => formatComma(record.buy_amount),
      },
      {
        title: "จำนวนเงินขาย (บาท)",
        dataIndex: "sell_amount",
        render: (text, record) => formatComma(record.sell_amount),
      },
      {
        title: "ส่วนต่าง ขาย-ซื้อ (บาท)",
        dataIndex: "diff_amount",
        render: (text, record) => formatComma(record.diff_amount),
      },
    ],
    []
  );

  const wood_grade_options = WOOD_IMPORT_GRADE_DISPLAY.map((grade) => {
    return { value: grade, key: grade, label: grade.toLocaleUpperCase() };
  });

  const truckCompanies = useSearchItem({
    url: URL_SCALE_LARGE.TRUCK_COMPANY,
    description_render: "name",
    title_render: "code",
  });

  const [truckCompanyCode, setTruckCompanyCode] = useState("");
  const searchText = Form.useWatch("truckCompanyName", form);
  const [debouncedText] = useDebounce(searchText, 500);
  const MIN_SEARCH_LENGTH = 2;

  const getQueryParams = () => {
    const values = form.getFieldsValue();
    const startDate = dayjs(values.dateRange[0]).format(DATE_FORMAT);
    const endDate = dayjs(values.dateRange[1]).format(DATE_FORMAT);
    const queryString = `date_after=${startDate}&date_before=${endDate}&grade=${grade}${
      truckCompanyCode ? `&truck_company_code=${truckCompanyCode}` : ""
    }`;
    return queryString;
  };

  const validateForm = () => {
    const values = form.getFieldsValue();
    const newErrors = {};
    if (!values.dateRange || values.dateRange.length < 2)
      newErrors.dateRange = t("error_messages.validation.required");
    if (!grade) newErrors.grade = t("error_messages.validation.required");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  React.useEffect(() => {
    if (debouncedText && debouncedText.length >= MIN_SEARCH_LENGTH) {
      truckCompanies.fetchData({ search: debouncedText });
    }
  }, [debouncedText]);

  React.useEffect(() => {
    if (validateForm()) {
      const params = getQueryParams();
      ref.current.refetch(params);
      setQueryParams(params);
    }
  }, [date, grade, truckCompanyCode]);

  return (
    <div>
      <Layout>
        <MainHeader activeItem={MENU_REPORT} />
        <Content style={{ margin: "24px", marginTop: "7em" }}>
          <Typography.Title level={2}>
            {t("menus.report_menus.wood_import_menus.wood_import_by_width")}
          </Typography.Title>

          <Form
            form={form}
            initialValues={{
              wood_grade_options: "ab",
              dateRange: [],
            }}
          >
            <Row gutter={16}>
              <Col sm={24} md={24} lg={6}>
                <Form.Item
                  label={t("filter")}
                  name="dateRange"
                  rules={[{ required: true, message: t("กรุณาเลือกช่วงเวลา") }]}
                >
                  <DatePicker.RangePicker
                    style={{ width: "100%" }}
                    placeholder={[
                      t("searchs.placeholders.startDate"),
                      t("searchs.placeholders.endDate"),
                    ]}
                    format={HUMAN_DATE_FORMAT}
                  />
                </Form.Item>
              </Col>

              <Col sm={24} md={24} lg={6}>
                <Form.Item
                  label={t("wood_imports.grade")}
                  name="wood_grade_options"
                >
                  <Select options={wood_grade_options} />
                </Form.Item>
              </Col>

              <Col sm={24} md={24} lg={6}>
                <Form.Item
                  label={t("wood_imports.truck_company")}
                  name="truckCompanyName"
                >
                  <AutoComplete
                    style={{ width: "100%" }}
                    placeholder={`${t("search")} ${t(
                      "searchs.fields.code"
                    )} ${t("or")} ${t("searchs.fields.name")}...`}
                    notFoundContent={searchText ? t("no_data") : ""}
                    onSearch={(value) => {
                      if (value === "") {
                        setTruckCompanyCode("");
                      } else {
                        form.setFieldsValue({ truckCompanyName: value });
                      }
                    }}
                    onSelect={(value, option) => {
                      setTruckCompanyCode(option.value);
                      form.setFieldsValue({
                        truckCompanyName: `${option.name}`,
                      });
                    }}
                    options={truckCompanies.response.map((company) => ({
                      value: company.code,
                      name: company.name,
                      label: (
                        <div>
                          <div>
                            <strong>{company.code}</strong>
                          </div>
                          <Typography.Text type="secondary">
                            {company.name}
                          </Typography.Text>
                        </div>
                      ),
                    }))}
                  >
                    <Input
                      suffix={
                        truckCompanies.isLoading ? (
                          <Spin
                            indicator={<LoadingOutlined spin />}
                            color="default"
                          />
                        ) : (
                          <SearchOutlined />
                        )
                      }
                      allowClear
                      disabled={truckCompanies.isLoading}
                    ></Input>
                  </AutoComplete>
                </Form.Item>
              </Col>
            </Row>

            <div style={{ textAlign: "right" }}>
              {["pdf", "excel"].map((type) => (
                <ReportExportButton
                  url={`${URL_BARCODE.REPORT_WOOD_IMPORT_WOOD_THICK_EXPORT}${type}/?${queryParams}`}
                  type={type}
                  title={`${t(
                    "menus.report_menus.wood_import_menus.wood_import_by_width"
                  )}.....`}
                  disabled={queryParams == null}
                />
              ))}
            </div>
          </Form>

          <ReportDetail
            ref={ref}
            reportUrl={URL_BARCODE.REPORT_WOOD_IMPORT_WOOD_THICK}
            columns={columns}
            isCustomSetData
            customSetData={(datas) => {
              const newData = [];
              let sumGoodVolume = 0;

              for (let data of datas) {
                for (let [
                  index,
                  wood_size_grade_map_data,
                ] of data.report.entries()) {
                  if (index === 0) {
                    newData.push({
                      mil_thick_code: data.mil_thick_code,
                      ...wood_size_grade_map_data,
                    });
                  } else {
                    newData.push(wood_size_grade_map_data);
                  }
                }

                const goodVolume = data.report.reduce(
                  (prev, cur) => prev + Number(cur.good_volume),
                  0
                );
                newData.push({
                  wood_code: "รวม",
                  total_woods: data.report.reduce(
                    (prev, cur) => prev + Number(cur.total_woods),
                    0
                  ),
                  total_volume: data.report.reduce(
                    (prev, cur) => prev + Number(cur.total_volume),
                    0
                  ),
                  good_woods: data.report.reduce(
                    (prev, cur) => prev + Number(cur.good_woods),
                    0
                  ),
                  good_volume: goodVolume,
                  percentage: goodVolume,
                  waste_woods: data.report.reduce(
                    (prev, cur) => prev + Number(cur.waste_woods),
                    0
                  ),
                  waste_volume: data.report.reduce(
                    (prev, cur) => prev + Number(cur.waste_volume),
                    0
                  ),
                  buy_amount: data.report.reduce(
                    (prev, cur) => prev + Number(cur.buy_amount),
                    0
                  ),
                  sell_amount: data.report.reduce(
                    (prev, cur) => prev + Number(cur.sell_amount),
                    0
                  ),
                  diff_amount: data.report.reduce(
                    (prev, cur) => prev + Number(cur.diff_amount),
                    0
                  ),
                });

                sumGoodVolume += goodVolume;
              }

              newData.forEach((e) => {
                if (e.wood_code === "รวม")
                  e.percentage = formatComma(
                    (e.percentage * 100) / sumGoodVolume
                  );
              });

              return newData;
            }}
          />
        </Content>
      </Layout>
    </div>
  );
}
